import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import { useEditorStats, DurationDistributionChart } from './EditorEngagementCard';
import {
  buildSignupUserDirectory,
  resolveIdentityFromSessionKey,
} from './accountCreatedEnrichment';
import { showSessionDistributionChart } from './analyticsThresholds';
import METRIC_DEFINITIONS from './metricDefinitions';
import { ANALYTICS_HEADLINE_NUMBER_SX } from './analyticsTypography';
import BottomSheet from './BottomSheet';
import DrilldownSectionHeader from './DrilldownSectionHeader';
import DrilldownAboutSection from './DrilldownAboutSection';
import MetricScopeDrilldownNote from './MetricScopeDrilldownNote';

const SESSION_BUCKETS = [
  { key: 'quick', label: 'Quick peek', range: 'Under 1 min' },
  { key: 'brief', label: 'Browsed', range: '1–5 min' },
  { key: 'standard', label: 'Engaged session', range: '5–15 min' },
  { key: 'long', label: 'Long session', range: '15+ min' },
];

const BUCKET_COLORS = {
  quick: 'rgba(0,0,0,0.18)',
  brief: 'rgba(0,0,0,0.36)',
  standard: 'rgba(0,0,0,0.62)',
  long: 'rgba(0,0,0,0.85)',
};

const formatSessionDuration = (ms) => {
  if (!ms) return 'open ended';
  const totalSecs = Math.round(ms / 1000);
  if (totalSecs < 60) return `${totalSecs}s`;
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const HeadlineColumn = ({ value, label }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
      {value.toLocaleString()}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: '0.875rem', md: '0.9375rem' },
        fontWeight: 500,
        color: 'rgba(0,0,0,0.5)',
        mt: 0.35,
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/**
 * Consolidated "Engagement" card — replaces the two stacked Shuffles and
 * Interactive Sessions tiles. Both numbers read off the same underlying
 * useEditorStats feed-rows pass, so giving them their own tile felt like
 * inflating one signal into two. Keeping them in one card means a single
 * drill-down (the session breakdown) instead of two near-identical sheets.
 *
 * The card thumbnail-strip idea from the design exploration is deferred
 * until the backend can hand us per-shuffle thumbnails reliably — see the
 * task notes.
 */
const engagementSummaryLine = (bucketCounts, total) => {
  if (!total) return null;
  let top = SESSION_BUCKETS[0];
  let topCount = bucketCounts?.[top.key] ?? 0;
  SESSION_BUCKETS.forEach((b) => {
    const c = bucketCounts?.[b.key] ?? 0;
    if (c > topCount) {
      top = b;
      topCount = c;
    }
  });
  if (topCount === 0) return null;
  return `Most sessions: ${top.label} (${top.range.toLowerCase()})`;
};

const EngagementCard = ({
  feedRows = [],
  loading = false,
  shuffleCount = 0,
  dateLabel = '',
  excludeInternal = false,
  excludePartnerAgents = false,
  partnerAgentEmailDomain = null,
  partnerAdminUsers = [],
  onDrillOpen,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(true);
  const [expandedBucket, setExpandedBucket] = useState(null);

  const {
    sessions,
    bucketCounts,
    interactiveActiveCount,
    measuredCount,
  } = useEditorStats(feedRows);

  const userDirectory = useMemo(
    () => buildSignupUserDirectory(partnerAdminUsers),
    [partnerAdminUsers]
  );

  const sessionIdentityLabel = (sessionKey) =>
    resolveIdentityFromSessionKey(sessionKey, feedRows, userDirectory).displayLabel;

  const interactiveSessionsCanonical = interactiveActiveCount || measuredCount || 0;

  const handleCardClick = () => {
    setInternalOpen(true);
    onDrillOpen?.();
  };

  const interactive = sessions.filter(
    (s) => s.hasEngagement && s.duration != null && s.duration > 5000
  );

  const defaultExpandedBucket = useMemo(() => {
    let bestKey = null;
    let bestCount = 0;
    SESSION_BUCKETS.forEach((b) => {
      const c = bucketCounts?.[b.key] ?? 0;
      if (c > bestCount) {
        bestCount = c;
        bestKey = b.key;
      }
    });
    return bestKey;
  }, [bucketCounts]);

  useEffect(() => {
    if (internalOpen) setExpandedBucket(defaultExpandedBucket);
  }, [internalOpen, defaultExpandedBucket]);

  const sessionSummary = engagementSummaryLine(
    bucketCounts,
    interactiveSessionsCanonical
  );

  const showDistributionChart = showSessionDistributionChart(interactiveSessionsCanonical);

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={220}
        sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
      />
    );
  }

  return (
    <>
      <ButtonBase
        onClick={handleCardClick}
        sx={{
          position: 'relative',
          width: '100%',
          textAlign: 'left',
          display: 'block',
          borderRadius: '14px !important',
          border: '1px solid rgba(0,0,0,0.06)',
          bgcolor: '#fff',
          p: { xs: 1.75, sm: 2 },
          transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' },
          '&:active': { transform: 'scale(0.997)' },
          cursor: 'pointer',
          textTransform: 'none',
          '& .MuiTypography-root': { textTransform: 'none' },
        }}
      >
        <Box sx={{ mb: { xs: 1, sm: 1.25 }, pr: 3 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TouchAppOutlinedIcon
              sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                color: 'rgba(0,0,0,0.5)',
                letterSpacing: '0.01em',
              }}
            >
              Engagement
            </Typography>
          </Stack>
          <Typography
            sx={{
              display: 'block',
              mt: 0.5,
              fontSize: '0.7rem',
              color: 'rgba(0,0,0,0.42)',
              lineHeight: 1.4,
              textAlign: 'left',
            }}
          >
            Active time only · 2+ min gaps excluded
          </Typography>
        </Box>

        <Stack
          direction="row"
          alignItems="stretch"
          spacing={{ xs: 2, sm: 3 }}
        >
          <HeadlineColumn
            value={interactiveSessionsCanonical}
            label="interactive sessions"
          />
          <HeadlineColumn
            value={shuffleCount}
            label="layouts generated with the shuffle button"
          />
        </Stack>

        {showDistributionChart && (
          <DurationDistributionChart
            bucketCounts={bucketCounts}
            total={interactiveSessionsCanonical}
          />
        )}

        <ChevronRightIcon
          sx={{
            position: 'absolute',
            top: { xs: 15, sm: 19 },
            right: { xs: 11, sm: 15 },
            fontSize: 18,
            color: 'rgba(0,0,0,0.3)',
            opacity: 0.45,
            pointerEvents: 'none',
          }}
        />
      </ButtonBase>

      <BottomSheet
        open={internalOpen}
        onClose={() => setInternalOpen(false)}
        title="Engagement"
        subtitle={dateLabel || undefined}
      >
        <MetricScopeDrilldownNote
          scope="demand"
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
        />
        {METRIC_DEFINITIONS.editorSessionsCard && (
          <DrilldownAboutSection
            body={METRIC_DEFINITIONS.editorSessionsCard.body}
            sections={METRIC_DEFINITIONS.editorSessionsCard.sections}
            sheetOpen={internalOpen}
          />
        )}

        {sessionSummary && (
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'rgba(0,0,0,0.5)',
              mb: 2,
              lineHeight: 1.45,
            }}
          >
            {sessionSummary}
            {shuffleCount > 0
              ? ` · ${shuffleCount.toLocaleString()} layout${shuffleCount !== 1 ? 's' : ''} generated`
              : ''}
          </Typography>
        )}

        <DrilldownSectionHeader
          title="Session breakdown"
          countLabel={
            interactive.length > 0
              ? `${interactive.length} session${interactive.length !== 1 ? 's' : ''}`
              : undefined
          }
          collapsible
          open={sessionsOpen}
          onToggle={() => setSessionsOpen((v) => !v)}
        />

        <Collapse in={sessionsOpen}>
          <Box
            sx={{
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: '#fff',
              overflow: 'hidden',
              mb: 1,
            }}
          >
            {interactive.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)', py: 2, textAlign: 'center' }}>
                No session details available.
              </Typography>
            ) : (
              SESSION_BUCKETS.map((bucket) => {
                const totalInBucket = bucketCounts?.[bucket.key] ?? 0;
                if (totalInBucket === 0) return null;
                const rowsInSlice = interactive.filter((s) => s.bucket === bucket.key);
                const isExpanded = expandedBucket === bucket.key;
                return (
                  <Box key={bucket.key}>
                    <ButtonBase
                      onClick={() =>
                        setExpandedBucket((k) => (k === bucket.key ? null : bucket.key))
                      }
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                        px: 1.5,
                        borderTop: '1px solid rgba(0,0,0,0.06)',
                        borderRadius: '0 !important',
                        textTransform: 'none !important',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '999px',
                          bgcolor: BUCKET_COLORS[bucket.key],
                          flexShrink: 0,
                          mr: 0.75,
                        }}
                      />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(0,0,0,0.72)' }}>
                        {bucket.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(0,0,0,0.38)', ml: 0.5 }}>
                        {bucket.range}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: 'rgba(0,0,0,0.5)',
                          fontVariantNumeric: 'tabular-nums',
                          mr: 0.5,
                        }}
                      >
                        {totalInBucket}
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          color: 'rgba(0,0,0,0.4)',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms ease',
                        }}
                      />
                    </ButtonBase>
                    <Collapse in={isExpanded}>
                      <Box>
                    {rowsInSlice.map((s, i) => {
                      const label = sessionIdentityLabel(s.key);
                      const actionParts = [
                        s.actionLabels?.includes('auto-furnished') ? 'auto-furnished' : null,
                        s.shuffleCount > 0
                          ? `${s.shuffleCount} shuffle${s.shuffleCount !== 1 ? 's' : ''}`
                          : null,
                        s.actionLabels?.includes('added items') ? 'added items' : null,
                      ].filter(Boolean);
                      const actionsStr =
                        actionParts.length > 0 ? actionParts.join(' · ') : 'viewed only';
                      return (
                        <Box
                          key={`${bucket.key}-${i}`}
                          sx={{
                            py: 1.25,
                            px: 2,
                            borderTop: '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: '0.8125rem',
                                  fontWeight: 500,
                                  color: 'rgba(0,0,0,0.78)',
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)', mt: 0.2 }}>
                                {actionsStr}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.78rem',
                                  fontWeight: 500,
                                  color: 'rgba(0,0,0,0.65)',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {formatSessionDuration(s.duration)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(0,0,0,0.35)', mt: 0.2 }}>
                                {formatTimestamp(s.openTs)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
            )}
          </Box>
        </Collapse>
      </BottomSheet>
    </>
  );
};

export default EngagementCard;
